import React, { useState, useEffect } from 'react';
import { Layout, Typography, Space, Dropdown, Button, message, Divider, Form, Input, Badge } from 'antd';
import { 
  DashboardOutlined, 
  PieChartOutlined, 
  HddOutlined, 
  UserOutlined, 
  LogoutOutlined,
  CaretDownOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
  MailOutlined,
  LockOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logoTop from './logo_top.png';
import EditBoxModal from './EditBoxModal';

const { Header } = Layout;
const { Text } = Typography;

const TopBar = () => {
  const [stats, setStats] = useState({ cpu: '0%', ram: '0%', disk: '0%' });
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : { username: 'User', id: null };

  // Logika za prikaz imena: Ime + Prezime ili Username
  const displayName = (user.first_name || user.last_name) 
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() 
    : user.username;

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/box/system-status/');
      setStats(response.data);
    } catch (error) {
      console.error('Greška:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    window.location.href = '/login';
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleUpdateProfile = async (values) => {
    setLoading(true);
    const payload = {
      id: user.id,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      password: values.password 
    };

    try {
      await axios.put(`/api/users/manage/`, payload);
      message.success('Profile updated successfully');
      
      const updatedUser = { ...user, ...values };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setIsProfileModalVisible(false);
      form.resetFields(['password']);
      window.location.reload(); // Osvežavamo da se povuku nova imena u TopBar
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getNum = (val) => {
    if (!val) return 0;
    const cleaned = String(val).replace(/[^0-9.]/g, ''); 
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  const cpuVal = getNum(stats.cpu);
  const ramVal = getNum(stats.ram);
  const diskVal = getNum(stats.disk);

  const isWarning = cpuVal > 80 || ramVal > 80 || diskVal > 80;
  const statusColor = isWarning ? '#ef4444' : '#34d399';
  const statusLabel = isWarning ? 'SERVER WARNING' : 'SERVER OK';

  const userMenuItems = {
    items: [
      { 
        key: 'profile', 
        label: 'My Profile', 
        icon: <UserOutlined />,
        onClick: () => setIsProfileModalVisible(true)
      }
    ]
  };

  return (
    <Header style={{ 
      background: '#1e293b', padding: 0, display: 'flex', alignItems: 'center', height: '65px',
      width: '100%', borderBottom: '1px solid #334155', position: 'fixed', top: 0, left: 0, zIndex: 1001
    }}>
      <div style={{ minWidth: '240px', maxWidth: '240px', width: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={logoTop} alt="logo" style={{ maxHeight: '45px', maxWidth: '100%', objectFit: 'contain' }} />
      </div>

      <div style={{ display: 'flex', flex: 1, height: '100%', position: 'relative', alignItems: 'center', padding: '0 24px' }}>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
          <Space size="middle">
            <Space style={{ background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: '6px', border: `1px solid ${statusColor}44`, minWidth: '150px', justifyContent: 'center' }}>
              {isWarning ? <WarningOutlined style={{ color: statusColor }} /> : <SafetyCertificateOutlined style={{ color: statusColor }} />}
              <Text style={{ color: statusColor, fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px' }}>{statusLabel}</Text>
            </Space>
            <Space style={{ background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <DashboardOutlined style={{ color: '#60a5fa' }} />
              <Text style={{ color: '#94a3b8', fontSize: '13px' }}>CPU: <span style={{ color: '#fff', fontWeight: '600' }}>{stats.cpu}</span></Text>
            </Space>
            <Space style={{ background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <PieChartOutlined style={{ color: '#34d399' }} />
              <Text style={{ color: '#94a3b8', fontSize: '13px' }}>RAM: <span style={{ color: '#fff', fontWeight: '600' }}>{stats.ram}</span></Text>
            </Space>
            <Space style={{ background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <HddOutlined style={{ color: '#fbbf24' }} />
              <Text style={{ color: '#94a3b8', fontSize: '13px' }}>DISK: <span style={{ color: '#fff', fontWeight: '600' }}>{stats.disk}</span></Text>
            </Space>
          </Space>
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <Space size="middle">
            <Dropdown menu={userMenuItems} placement="bottomRight">
              <Space style={{ cursor: 'pointer', padding: '0 8px' }}>
                <UserOutlined style={{ color: '#fff', fontSize: '16px' }} />
                <Text style={{ color: '#fff', fontWeight: '500' }}>{displayName}</Text>
                <CaretDownOutlined style={{ color: '#64748b', fontSize: '10px' }} />
              </Space>
            </Dropdown>
            <Button type="primary" danger size="small" icon={<LogoutOutlined />} style={{ borderRadius: '6px', background: '#ef4444', border: 'none' }} onClick={handleLogout}>
              Logout
            </Button>
          </Space>
        </div>
      </div>

      <EditBoxModal 
        title="Edit My Profile" 
        isVisible={isProfileModalVisible} 
        onCancel={() => setIsProfileModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateProfile}
          initialValues={{
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email
          }}
        >
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <Text style={{ color: '#64748b', fontSize: '12px' }}>Username: </Text>
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>{user.username}</Text>
            <br />
            <Badge status="processing" text={<span style={{ color: '#3b82f6', fontSize: '11px' }}>{user.is_superuser ? 'SYSTEM ADMIN' : 'OPERATOR'}</span>} />
          </div>

          <Space style={{ width: '100%' }} direction="vertical" size={0}>
            <Form.Item name="first_name" label={<span style={{ color: '#94a3b8' }}>First Name</span>}>
              <Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
            </Form.Item>
            <Form.Item name="last_name" label={<span style={{ color: '#94a3b8' }}>Last Name</span>}>
              <Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
            </Form.Item>
            <Form.Item name="email" label={<span style={{ color: '#94a3b8' }}>Email Address</span>} rules={[{ type: 'email' }]}>
              <Input prefix={<MailOutlined style={{ color: '#64748b' }} />} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
            </Form.Item>
            <Divider style={{ borderColor: '#334155', margin: '12px 0' }} />
            <Form.Item name="password" label={<span style={{ color: '#94a3b8' }}>New Password (leave blank to keep old)</span>}>
              <Input.Password prefix={<LockOutlined style={{ color: '#64748b' }} />} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
            </Form.Item>
          </Space>
        </Form>
      </EditBoxModal>
    </Header>
  );
};

export default TopBar;